import { useWizard, WizardStep } from '@zillow/zrs-rental-applications';
import {
  useId,
  useIdVerificationCallbacks,
  usePassiveAuthData,
  usePidAnswers,
  useReauthenticating,
  useTermSet,
} from '../../contexts/IdVerificationContext';
import { useIdVerificationWebApiHandler_performIdentityCheck } from '@zg-rentals/ts-id-verification-client-react-query';
import { Flex, Heading, LoadingButton, Paragraph } from '@zillow/constellation';
import { TermSet } from '../../../..';
import { LANDLORD_CLICKSTREAM_EVENTS } from '../../clickstream/events';
import { LANDLORD_METRIC_PREFIX, RENTER_METRIC_PREFIX } from '../../IdVerification';
import { IdvStep, LogLevel } from '../../../../models';
import { useQueryClient } from '@zg-rentals/react-query5';
import type { ButtonProps } from '../../NextButton/NextButton';
import { getZgAuthZoneFromTermSet } from '../../utils/zgAuthZoneUtils';
import { submitIdentityAnswersMutationKey } from '../KiqStep/KiqStep';

interface KiqOnboardingStepProps {
  id: string;
  label: string;
}

export default function KiqOnboardingStep({ id, label }: KiqOnboardingStepProps) {
  const queryClient = useQueryClient();
  const mutations = queryClient.getMutationCache().getAll();
  const hasSubmittedIdentityAnswers = mutations.some(
    (mutation) => JSON.stringify(mutation?.options?.mutationKey) === JSON.stringify(submitIdentityAnswersMutationKey),
  );
  const { currentStep, onRequestStep } = useWizard();
  const termSet = useTermSet();
  const requestingClient = useId();
  const pidAnswers = usePidAnswers();
  const { jsc, hdimPayload } = usePassiveAuthData();
  const reauthenticating = useReauthenticating();
  const { trackEvent, emitMetric, log } = useIdVerificationCallbacks();
  const { mutateAsync } = useIdVerificationWebApiHandler_performIdentityCheck({
    onMutate() {
      const hasPassiveAuthData = hdimPayload && jsc;
      if (!hasPassiveAuthData) {
        if (termSet === TermSet.LandlordScreening) {
          emitMetric(`${LANDLORD_METRIC_PREFIX} missingJscOrHdim`);
        } else {
          emitMetric(`${RENTER_METRIC_PREFIX} missingJscOrHdim`);
        }
      }
    },
    onError(error) {
      if (termSet === TermSet.LandlordScreening) {
        emitMetric(`${LANDLORD_METRIC_PREFIX} performIdentityCheck error`);
      } else {
        emitMetric(`${RENTER_METRIC_PREFIX} performIdentityCheck error`);
      }
      log(LogLevel.Error, {
        message: 'Error calling performPassiveIdentityCheck for KIQ questions',
        error,
      });
    },
  });
  return (
    <WizardStep
      id={id}
      label={label}
      hidden={id !== currentStep}
      onRequestNext={async (abortController: AbortController) => {
        abortController.abort();
        if (hasSubmittedIdentityAnswers) {
          await mutateAsync({
            postData: {
              ...pidAnswers,
              deviceType: 'desktop',
              requestingClient,
              zgAuthZone: getZgAuthZoneFromTermSet(termSet),
              isReAuthRequest: reauthenticating,
              hdimPayload,
              jsc,
            },
          });
        }
        if (termSet === TermSet.LandlordScreening) {
          trackEvent(LANDLORD_CLICKSTREAM_EVENTS.clickNextOntoKBA);
        }
        onRequestStep(IdvStep.KiqStep);
      }}
      meta={{
        renderNextButton: (buttonProps: ButtonProps) => (
          <LoadingButton {...buttonProps} buttonType="primary">
            Next
          </LoadingButton>
        ),
        renderBackButton: () => null,
      }}
      render={() => (
        <Flex display="flex" flexDirection="column" gap="sm">
          <Heading level={4} as="h2">
            Answer identity verification questions
          </Heading>
          <Paragraph>
            On the next screen, you’ll be asked a few questions to complete the verification process.
          </Paragraph>
          <Paragraph>
            After you choose <b>Next</b>, you’ll have <b>5 minutes</b> to complete this step.
          </Paragraph>
          <Paragraph>Don’t refresh the page while answering questions.</Paragraph>
        </Flex>
      )}
    />
  );
}
